import { AxiosResponse } from 'axios';
import { Module } from 'vuex';
import {
  SET_ERRORMSG,
  SET_WEEKLY_SCHEDULE_N,
  SET_WEEKLY_SCHEDULE_N_RECORD,
  SET_WEEKLY_SCHEDULES_N,
} from '@/store/mutation_types';
import {
  ACTIVATE_WEEKLY_SCHEDULE_TEMPLATE_N,
  CLEAR_WEEKLY_SCHEDULE_N_ISLOADING,
  COPY_WEEKLY_SCHEDULE_DAY_N,
  DELETE_WEEKLY_SCHEDULE_DAY_NOTE_N,
  DELETE_WEEKLY_SCHEDULE_ITEM_N,
  DELETE_WEEKLY_SCHEDULE_N,
  DELETE_WEEKLY_SCHEDULE_OVERRIDE_N,
  DELETE_WEEKLY_SCHEDULE_TEMPLATE_N,
  DUPLICATE_WEEKLY_SCHEDULE_ITEM_N,
  DUPLICATE_WEEKLY_SCHEDULE_N,
  DUPLICATE_WEEKLY_SCHEDULE_TEMPLATE_N,
  EDIT_WEEKLY_SCHEDULE_ITEM_N,
  EDIT_WEEKLY_SCHEDULE_N,
  EDIT_WEEKLY_SCHEDULE_TEMPLATE_N,
  GET_WEEKLY_SCHEDULE_N,
  GET_WEEKLY_SCHEDULES_N,
  REGISTER_WEEKLY_SCHEDULE_ITEM_N,
  REGISTER_WEEKLY_SCHEDULE_N,
  REGISTER_WEEKLY_SCHEDULE_TEMPLATE_N,
  SAVE_WEEKLY_SCHEDULE_DAY_NOTE_N,
  SAVE_WEEKLY_SCHEDULE_DISPLAYS_N,
  SAVE_WEEKLY_SCHEDULE_OVERRIDE_N,
  SAVE_WEEKLY_SCHEDULE_SETTINGS_N,
  TOGGLE_WEEKLY_SCHEDULE_N,
  VERIFY_WEEKLY_SCHEDULE_ITEM_N,
} from '@/store/action_types';
import { StateObject } from '@/dtos/state.dto';
import WeeklySchedule_N from '@/services/weekly_schedule_N';

/* ---------------------------------------------------------------------------
   Three slices: `weeklySchedules_N` is the list page's summary of every
   schedule, `weeklySchedule_N` is the whole bundle the open schedule's page
   works from (its templates, items, notes, displays, settings and the rooms),
   and `weeklyScheduleRecord_N` is whatever the last write answered with, plus
   the loading flag every modal's Save button reads. Every write resolves to
   the record the API answered (or false), and the page re-reads after it —
   one request, and the page can never drift from the server.
   ------------------------------------------------------------------------- */

const run = async (
  commit: any,
  request: () => Promise<AxiosResponse>
): Promise<any> => {
  commit(SET_WEEKLY_SCHEDULE_N_RECORD, { data: null, isLoading: true });
  let result: AxiosResponse | undefined;
  try {
    result = await request();
  } catch (error) {
    commit(SET_WEEKLY_SCHEDULE_N_RECORD, { isLoading: false });
    throw error;
  }
  if (result && result.data && result.data.status) {
    commit(SET_WEEKLY_SCHEDULE_N_RECORD, { data: result.data.data, isLoading: false });
    return result.data.data ?? true;
  }
  commit(SET_WEEKLY_SCHEDULE_N_RECORD, { data: null, isLoading: false });
  if (result && result.data) commit(SET_ERRORMSG, result.data.msg);
  return false;
};

// A read into one slice. What it held stays on screen while it reloads, so a
// save never blanks the page for the round trip. An answer `isCurrent` says
// has been overtaken by a newer read is handed back without landing.
const read = async (
  commit: any,
  mutation: string,
  request: () => Promise<AxiosResponse>,
  isCurrent: () => boolean = () => true
): Promise<any> => {
  commit(mutation, { isLoading: true });
  let result: AxiosResponse | undefined;
  try {
    result = await request();
  } catch (error) {
    if (isCurrent()) commit(mutation, { isLoading: false });
    return false;
  }
  if (!isCurrent()) return result && result.data && result.data.status ? result.data.data : false;
  if (result && result.data && result.data.status) {
    commit(mutation, { data: result.data.data, isLoading: false });
    return result.data.data;
  }
  commit(mutation, { isLoading: false });
  if (result && result.data) commit(SET_ERRORMSG, result.data.msg);
  return false;
};

// Only the newest bundle read lands, so a schedule opened after another is
// never overwritten by the earlier one's late answer.
let bundleRead = 0;

const weeklySchedule_N: Module<any, {}> = {
  state: {
    weeklySchedules_N: {
      data: [],
      isLoading: false,
    },
    weeklySchedule_N: {
      data: null,
      isLoading: false,
    },
    weeklyScheduleRecord_N: {
      data: null,
      isLoading: false,
    },
  },
  getters: {
    weeklySchedules_N: (state: { weeklySchedules_N: any }) => state.weeklySchedules_N,
    weeklySchedule_N: (state: { weeklySchedule_N: any }) => state.weeklySchedule_N,
    weeklyScheduleRecord_N: (state: { weeklyScheduleRecord_N: any }) => state.weeklyScheduleRecord_N,
  },
  mutations: {
    [SET_WEEKLY_SCHEDULES_N]: (
      state: { weeklySchedules_N: StateObject },
      payload: { data?: any; isLoading: boolean }
    ) => {
      if (payload.data != null) state.weeklySchedules_N.data = payload.data;
      state.weeklySchedules_N.isLoading = payload.isLoading;
    },
    [SET_WEEKLY_SCHEDULE_N]: (
      state: { weeklySchedule_N: StateObject },
      payload: { data?: any; isLoading: boolean }
    ) => {
      if (payload.data != null) state.weeklySchedule_N.data = payload.data;
      state.weeklySchedule_N.isLoading = payload.isLoading;
    },
    [SET_WEEKLY_SCHEDULE_N_RECORD]: (
      state: { weeklyScheduleRecord_N: StateObject },
      payload: { data?: any; isLoading: boolean }
    ) => {
      if (payload.data != null) state.weeklyScheduleRecord_N.data = payload.data;
      state.weeklyScheduleRecord_N.isLoading = payload.isLoading;
    },
  },
  actions: {
    /* ------------------------------ schedules ------------------------------ */
    [GET_WEEKLY_SCHEDULES_N]: ({ commit }: any, payload: any) =>
      read(commit, SET_WEEKLY_SCHEDULES_N, () => WeeklySchedule_N.getWeeklySchedules_N(payload || {})),
    // `payload.id` is the schedule; its page checks the bundle it holds is
    // that schedule's before drawing it.
    [GET_WEEKLY_SCHEDULE_N]: ({ commit }: any, payload: any) => {
      const ticket = ++bundleRead;
      return read(
        commit,
        SET_WEEKLY_SCHEDULE_N,
        () => WeeklySchedule_N.getWeeklySchedule_N(payload),
        () => ticket === bundleRead
      );
    },

    [REGISTER_WEEKLY_SCHEDULE_N]: ({ commit }: any, payload: any) =>
      run(commit, () => WeeklySchedule_N.registerSchedule_N(payload)),
    [EDIT_WEEKLY_SCHEDULE_N]: ({ commit }: any, payload: any) =>
      run(commit, () => WeeklySchedule_N.editSchedule_N(payload)),
    [TOGGLE_WEEKLY_SCHEDULE_N]: ({ commit }: any, payload: any) =>
      run(commit, () => WeeklySchedule_N.toggleSchedule_N(payload)),
    [DUPLICATE_WEEKLY_SCHEDULE_N]: ({ commit }: any, payload: any) =>
      run(commit, () => WeeklySchedule_N.duplicateSchedule_N(payload)),
    [DELETE_WEEKLY_SCHEDULE_N]: ({ commit }: any, id: string) =>
      run(commit, () => WeeklySchedule_N.deleteSchedule_N(id)),

    /* ------------------------------ templates ------------------------------ */
    [REGISTER_WEEKLY_SCHEDULE_TEMPLATE_N]: ({ commit }: any, payload: any) =>
      run(commit, () => WeeklySchedule_N.registerTemplate_N(payload)),
    [EDIT_WEEKLY_SCHEDULE_TEMPLATE_N]: ({ commit }: any, payload: any) =>
      run(commit, () => WeeklySchedule_N.editTemplate_N(payload)),
    [ACTIVATE_WEEKLY_SCHEDULE_TEMPLATE_N]: ({ commit }: any, payload: any) =>
      run(commit, () => WeeklySchedule_N.activateTemplate_N(payload)),
    [DUPLICATE_WEEKLY_SCHEDULE_TEMPLATE_N]: ({ commit }: any, payload: any) =>
      run(commit, () => WeeklySchedule_N.duplicateTemplate_N(payload)),
    // `{ id, scheduleId }`: a season lives on its schedule.
    [DELETE_WEEKLY_SCHEDULE_TEMPLATE_N]: ({ commit }: any, payload: any) =>
      run(commit, () => WeeklySchedule_N.deleteTemplate_N(payload)),

    /* -------------------------------- items -------------------------------- */
    [REGISTER_WEEKLY_SCHEDULE_ITEM_N]: ({ commit }: any, payload: any) =>
      run(commit, () => WeeklySchedule_N.registerItem_N(payload)),
    [EDIT_WEEKLY_SCHEDULE_ITEM_N]: ({ commit }: any, payload: any) =>
      run(commit, () => WeeklySchedule_N.editItem_N(payload)),
    [DUPLICATE_WEEKLY_SCHEDULE_ITEM_N]: ({ commit }: any, payload: any) =>
      run(commit, () => WeeklySchedule_N.duplicateItem_N(payload)),
    [COPY_WEEKLY_SCHEDULE_DAY_N]: ({ commit }: any, payload: any) =>
      run(commit, () => WeeklySchedule_N.copyDay_N(payload)),
    [VERIFY_WEEKLY_SCHEDULE_ITEM_N]: ({ commit }: any, payload: any) =>
      run(commit, () => WeeklySchedule_N.verifyItem_N(payload)),
    [DELETE_WEEKLY_SCHEDULE_ITEM_N]: ({ commit }: any, id: string) =>
      run(commit, () => WeeklySchedule_N.deleteItem_N(id)),

    /* ------------------------- overrides, day notes ------------------------ */
    [SAVE_WEEKLY_SCHEDULE_OVERRIDE_N]: ({ commit }: any, payload: any) =>
      run(commit, () => WeeklySchedule_N.saveOverride_N(payload)),
    // `{ id, itemId }`: a date change lives on its item.
    [DELETE_WEEKLY_SCHEDULE_OVERRIDE_N]: ({ commit }: any, payload: any) =>
      run(commit, () => WeeklySchedule_N.deleteOverride_N(payload)),

    [SAVE_WEEKLY_SCHEDULE_DAY_NOTE_N]: ({ commit }: any, payload: any) =>
      run(commit, () => WeeklySchedule_N.saveDayNote_N(payload)),
    // `{ id, scheduleId }`: a day note lives on its schedule.
    [DELETE_WEEKLY_SCHEDULE_DAY_NOTE_N]: ({ commit }: any, payload: any) =>
      run(commit, () => WeeklySchedule_N.deleteDayNote_N(payload)),

    /* ------------------------------- displays ------------------------------ */
    [SAVE_WEEKLY_SCHEDULE_DISPLAYS_N]: ({ commit }: any, payload: any) =>
      run(commit, () => WeeklySchedule_N.saveDisplays_N(payload)),

    /* ------------------------------- settings ------------------------------ */
    [SAVE_WEEKLY_SCHEDULE_SETTINGS_N]: ({ commit }: any, payload: any) =>
      run(commit, () => WeeklySchedule_N.saveSettings_N(payload)),

    [CLEAR_WEEKLY_SCHEDULE_N_ISLOADING]: ({ commit }: any) => {
      commit(SET_WEEKLY_SCHEDULES_N, { isLoading: false });
      commit(SET_WEEKLY_SCHEDULE_N, { isLoading: false });
      commit(SET_WEEKLY_SCHEDULE_N_RECORD, { isLoading: false });
    },
  },
};

export default weeklySchedule_N;
